import { Badge, DataTable, type DataTableColumn } from "@design";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, CircleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { adminMicrosoftPermissionsOptions, type MicrosoftFeature } from "@/api";
import { PageSection } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { formatList } from "@/i18n/format";
import { useCapabilities } from "../../account/useCurrentUser";

/**
 * Microsoft 365 permissions: what AnthmAI can do in Microsoft 365 here, feature by feature, and the exact permissions
 * an admin still needs to grant on the App Registration for the rest. Features this deployment doesn't offer aren't
 * listed. Nothing is ever asked of people at sign-in: a feature works once its permissions are granted.
 */
export function MicrosoftPermissionsSection() {
  const { t } = useTranslation("adminIntegrations", { keyPrefix: "microsoft" });
  const { sharepointEnabled } = useCapabilities();
  const permissions = useQuery({
    ...adminMicrosoftPermissionsOptions(),
    enabled: sharepointEnabled,
    select: (r) => r.data,
  });
  if (!sharepointEnabled) return null;
  const rows = (permissions.data?.features ?? []).filter((f) => f.offered);

  const columns: DataTableColumn<MicrosoftFeature>[] = [
    {
      key: "feature",
      header: t("columns.feature"),
      mobile: "primary",
      render: (row) => (
        <div className="flex flex-col">
          <Text as="span" variant="body-sm">
            {t(`feature.${row.key}` as "feature.files")}
          </Text>
          <Text as="span" variant="caption" tone="tertiary">
            {formatList(row.permissions)}
          </Text>
        </div>
      ),
    },
    {
      key: "status",
      header: t("columns.status"),
      render: (row) =>
        row.granted ? (
          <Badge variant="success" icon={<CheckCircle2 size={12} />}>
            {t("granted")}
          </Badge>
        ) : (
          <div className="flex flex-col items-start gap-1">
            <Badge variant="warning" icon={<CircleAlert size={12} />}>
              {t("missing")}
            </Badge>
            <Text as="span" variant="caption" tone="tertiary">
              {t("grant", { permissions: formatList(row.missing) })}
            </Text>
          </div>
        ),
    },
  ];

  return (
    <PageSection title={t("title")} description={t("description")}>
      {permissions.isError && (
        <Text tone="error" role="alert">
          {t("loadFailed")}
        </Text>
      )}
      {permissions.data && !permissions.data.checked ? (
        <Text variant="body-sm" tone="secondary">
          {t("unchecked")}
        </Text>
      ) : (
        <DataTable
          aria-label={t("title")}
          columns={columns}
          rows={rows}
          rowKey={(row) => row.key}
          loading={permissions.isPending}
          empty={
            <Text variant="body-sm" tone="tertiary">
              {t("empty")}
            </Text>
          }
        />
      )}
    </PageSection>
  );
}
